import { StylesManager, Model } from 'survey-core';
import { Survey } from 'survey-vue-ui';
import Loader from "@/components/loader/Loader.vue";
import firebase from "firebase";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { serializer } from "@/api/shared/serializer";
import {getRegionName} from "@/utils/functionsRegion";
import { VisitorRoleEnum } from '@/enums/VisitorRoleEnum';
import { getBase64SizeInMB, uploadFileByBase64 } from '@/utils/upload-image';
import { v4 as uuidv4 } from 'uuid';
import { RouteGroup } from '@/router/routeGroup';

StylesManager.applyTheme("modern");

export default {
    name: "surveyForm",
    components: {
        Survey,
        Loader,
        ValidationObserver,
        ValidationProvider
    },
    data() {
        return {
            loading: true,
            loadingForm: false,
            surveyModel: null,
            surveyform: this.surveyForm,
            surveyResult: null,
            dialog: false,
            isResident: true,
            uploadedFile: null,
            errorMessage: null,
            preliminaryQuestions: {
                isHappy: "",
                isAssistant: true,
                assistantType: ""
            }
        };
    },
    computed: {
        survey() {
            return this.$store.state.surveyModule.currentSurvey;
        },
        surveyForm() {
            return this.$store.state.surveyModule.currentSurveyForm;
        }
    },
    async created() {
        const surveyId = this.$route.params.id;
        window.addEventListener("beforeunload", this.leaving);

        if (this.$route.name === "SurveyGuestbook") {
            await this.$store.dispatch("getSurvey", { id: surveyId, isRoute: false });
            await this.$store.dispatch("getSurveyForm", { id: surveyId });
            this.openPreliminaryQuestions();
            this.createSurveyForm();
            this.loading = false;
            return false;
        }

        if (this.$route.params?.token?.length) {
            await this.$store.dispatch("signInByCustomToken", { customToken: this.$route.params.token, isExternalPagesViewing: true })
            const premiumFeatures = this.$store.getters.premiumFeatures;
            if(premiumFeatures.isStaffHubSurveys === false){
                await this.$store.dispatch("userSignOut")
            }
       }

        if(this.$route.name === "SurveyEmail") {
            if(this.$route.params.visitorId && this.$route.params.visitorId?.length) {

                const claims: any = { accessGroups: [RouteGroup.Surveys] }
                const role = this.$route.params?.visitorRole

                if(role === VisitorRoleEnum.FamilyMember){
                    claims.isFamily = true
                } else if (role === VisitorRoleEnum.Resident){
                    claims.isResident = true
                } else if (role === VisitorRoleEnum.Staff){
                    claims.isStaff = true
                }

                const superAdminCustomTokenResult = await firebase.app().functions(getRegionName()).httpsCallable("getUserCustomToken")({
                    userId: this.$route.params.visitorId,
                    userType: role,
                    claims: claims
                })
                const superAdminTokenCustomToken = superAdminCustomTokenResult?.data?.customToken;
                await this.$store.dispatch("signInByCustomToken", { customToken: superAdminTokenCustomToken, isExternalPagesViewing: true })
                await this.$store.dispatch("getSurvey", { id: surveyId, isRoute: false });
                await this.getExistSurveyResultDoc(surveyId, this.$route.params.visitorId);
                await this.$store.dispatch("getSurveyForm", { id: surveyId });
                this.openPreliminaryQuestions();
                this.createSurveyForm();
                return false;
            }
        }

        await this.$store.dispatch("getSurvey", { id: surveyId, isRoute: false });
        await this.getExistSurveyResultDoc(surveyId, this.$route.params.r);
        this.openPreliminaryQuestions();
        this.createSurveyForm();
        this.loading = false;
    },
    async beforeDestroy() {
        if(this.$store.getters?.user?.isExternalPagesViewing === true){
            await this.$store.dispatch("userSignOut")
        }
    },
    methods: {  
        createSurveyForm() {
            const surveyForm = this.surveyForm;
            const surveyResult = this.surveyResult?.surveyResult;

            if (surveyForm) {
     
                this.surveyModel = new Model(surveyForm);
             
                this.surveyModel.onValueChanging.add(async (result, options) => {

                    const isFileCleared = (typeof(options.value) === 'undefined') 
                    && options.oldValue?.hasOwnProperty('content') && (options.oldValue.type.startsWith('image') || options.oldValue.type.startsWith('video'))
                    
                    if(isFileCleared){
                        options.value = []
                    }

                    if(typeof(options.value) === 'undefined' && !isFileCleared) return

                    if(Array.isArray(options.value) && options.value[0]?.hasOwnProperty('content') && options.value[0].content?.includes('base64')) {
                        const sizeMB = getBase64SizeInMB(options.value[0].content)

                        if(sizeMB <= 133){
                            this.errorMessage = null
                            await this.saveSurveyResult(options.name, options.value)
                        } else{
                            this.errorMessage = `The selected file is too large. Maximum allowed size is 133 MB. Your file is ${sizeMB.toFixed(2)} MB.`
                        }
                    }
                    else{
                        await this.saveSurveyResult(options.name, options.value)
                    }

                })

                this.surveyModel.onComplete.add((sender) => {
                      console.log("COMPLETE: sending full survey data", sender.data);
                    this.createSurveyResult(sender.data);
                });

                if (surveyResult && Object.keys(surveyResult).length) {
                    Object.entries(surveyResult).forEach(entry => {
                        const [key, value] = entry;
                        this.surveyModel.setValue(key, value);
                    });
                }

                this.surveyform = this.surveyModel;
            }

            this.loading = false
        },
        async createSurveyResult(surveyResultData: any) {
            const result = surveyResultData

            if (this.$route.name == "SurveyGuestbook") {
                const survey = this.$route.params.id;
                const visitId = this.$route.params.visitId;
                const visitorId = this.$route.params.visitorId;
                     
                await this.$store.dispatch("submitGuestbookSurveyResult", {
                    surveyId: survey,
                    visitId: visitId,
                    visitorId: visitorId,
                    surveyResult: result,
                });
            } else {
                const survey = this.$route.params.id;
                await this.$store.dispatch("submitSurveyResult", {
                    surveyId: survey,
                    id: this.surveyResult.id,
                    r: this.$route.params.r ?? this.$route.params.visitorId,
                    surveyResult: result
                });
            }
        },
       async saveSurveyResult(name, value) {
            if (["SurveyForm", "SurveyEmail", "SurveyForm By Token"].includes(this.$route.name)) {
                this.surveyModel.allowComplete = false;

                try {
                    const doc = await firebase.firestore().collection("surveyResults").doc(this.surveyResult.id).get();

                    if (!doc.exists) {
                        console.warn("Document not found for surveyResult ID", this.surveyResult.id);
                        return;
                    }

                    const docDoc = doc.data();

                    if (!docDoc.surveyResult || typeof docDoc.surveyResult !== 'object') {
                        docDoc.surveyResult = {};
                    }

                    docDoc.surveyResult[name] = value;

                    if (Array.isArray(value) && value[0]?.hasOwnProperty('content') && value[0].content?.includes('base64')) {
                        docDoc.surveyResult[name] = await this.uploadMedia(value[0], docDoc.facilityId);
                        docDoc.surveyResult[name].storeDataAsText = false;
                    }

                    const updates = {
                        surveyResult: docDoc.surveyResult,
                        isCompletedByEmail: this.$route.name === "SurveyEmail"
                    };

                    await doc.ref.update(updates);
                    await firebase.firestore().collection("surveyResults").doc(this.surveyResult.id).update(updates);

                    this.surveyResult.surveyResult = docDoc.surveyResult;

                } catch (error) {
                    console.error("Error saving survey result:", error);
                } finally {
                    this.surveyModel.allowComplete = true;
                }
            }
        },
        async uploadMedia(fileData: any, facilityId: string) {
            const surveyId = this.$route.params.id;

              if (fileData.content && fileData.content?.includes('base64')) {
                const base64 = fileData.content;
          
                if (fileData.type?.startsWith("image/") || fileData.type?.startsWith("video/")) {
                  const fileExt = fileData.type.split("/")[1];
                  const fileName = `${uuidv4()}.${fileExt}`;
                  const path = `${facilityId}/surveys/${surveyId}/${fileName}`;
          
                  const uploadedFileUrl = await uploadFileByBase64({
                    path,
                    base64: base64,
                    onStart: () => {
                      console.log(`start uploading ${fileData.type} file`);
                    },
                    onError: (error) => {
                      console.log(`error uploading ${fileData.type} file:`, error);
                    }
                  });
          
                  if (uploadedFileUrl) {
                    this.uploadedFile = { name: fileName, url: uploadedFileUrl, path: path, fileExt: fileExt}
                    console.log(`file ${fileData.type} uploaded. url:`, uploadedFileUrl);

                    fileData.name = fileName
                    fileData.content = uploadedFileUrl

                    return fileData
                  }
                }
              }
          
            return null;
        },
        async getExistSurveyResultDoc(surveyId, r) {
            const { facilityId, orgId } = (await firebase.firestore().collection("surveys").doc(surveyId).get()).data()
            const loggedUser = this.$store.state.authModule.user;

            if(loggedUser && facilityId !== loggedUser.facilityId) {
                this.$toast.open({
                    message: `You're logged into ${loggedUser.facilities.find(x => x.id === loggedUser.facilityId).name}, but this survey is for ${loggedUser.facilities.find(x => x.id === facilityId).name}. Please sign out and reopen the link.`,
                    type: "error"
                });

                throw new Error(`You're logged into ${loggedUser.facilities.find(x => x.id === loggedUser.facilityId).name}, but this survey is for ${loggedUser.facilities.find(x => x.id === facilityId).name}. Please sign out and reopen the link.`);
            }

            await firebase
                .firestore()
                .collection("surveyResults")
                .where("facilityId", "==", facilityId)
                .where("surveyId", "==", surveyId)
                .where("r", "==", r)
                .get()
                .then(async data => {
                    const surveyResult = serializer.querySnapshotToDocuments(data);

                    if (surveyResult.length) {
                        await this.$store.dispatch("getSurveyForm", { id: surveyId })
                        this.surveyResult = {
                            id: surveyResult[0].id,
                            ...surveyResult[0]
                        }
                    } else {

                        await firebase
                            .firestore()
                            .collection("surveyResults")
                            .add({
                                facilityId: facilityId,
                                surveyId: surveyId,
                                r: r,
                                surveyResult: {},
                                isCompleted: false,
                                submittedDateTime: new Date(),
                                orgId: orgId,
                            }).then(async data => {
                                this.surveyResult = { id: data.id, surveyResult: {} };
                                await this.$store.dispatch("getSurveyForm", { id: surveyId })
                            })

                        await firebase
                            .firestore()
                            .collection("surveys")
                            .doc(surveyId)
                            .update({
                                'inProgress': firebase.firestore.FieldValue.arrayUnion(r)
                            });
                    }
                }).catch(async (error) => {
                    this.$toast.open({message: 'Unable to load survey', type: "error"});
                    throw new Error("Unable to load survey:  " + error.message);
                })
        },
        async openPreliminaryQuestions() {
            const surveyResult = this.surveyResult;

            await this.checkIfResident();

            if (this.survey?.isPreliminaryQuestions && this.isResident) {
                this.dialog = true;
            }

            if (surveyResult && Object.prototype.hasOwnProperty.call(surveyResult, 'isHappy')) {
                this.preliminaryQuestions.isHappy = surveyResult?.isHappy;
            }

            if (surveyResult && Object.prototype.hasOwnProperty.call(surveyResult, 'assistantType')) {
                this.preliminaryQuestions.assistantType = surveyResult?.assistantType;
            }
        },
        async checkIfResident() {
            const id = this.$route.params.r;
            if(id) {
                await firebase
                  .firestore()
                  .collection("residents")
                  .doc(id)
                  .get()
                  .then((data) => {
                      const resident = serializer.documentSnapshotToDocument(data);
                      if (!resident.id) {
                          this.isResident = false;
                      }
                  }).catch(() => {
                      this.isResident = false;
                  })
            }
        },
        async completePreliminaryQuestion() {
            this.loadingForm = true;

            if (this.preliminaryQuestions.assistantType === "none") {
                this.preliminaryQuestions.isAssistant = false;
            }

            await firebase
                .firestore()
                .collection("surveyResults")
                .doc(this.surveyResult.id)
                .update({
                    ...this.preliminaryQuestions
                }).then((data) => {
                    this.loadingForm = false;
                    this.dialog = false;
                }).catch(error => {
                    alert("COMPLETE PRELIMINARY QUESTION ERROR " + error.message)
                })
        },
        async leaving() {
            if(this.$store.getters?.user?.isExternalPagesViewing === true){
                await this.$store.dispatch("userSignOut")
            }
        }
    },
};